import { redirectToLogin, tryRefreshToken } from '../common/auth'

// Resolve the api2 host from the current page host. Prod uses
// `api2.contractfinderpro.com`; a beta env (site hosted at e.g.
// `tenderninja.beta5.contractfinderpro.com`) rewrites to
// `api2-beta5.contractfinderpro.com` so the API travels with the env.
// Returns `null` for localhost so callers can fall back to relative URLs
// (the Vite dev proxy forwards to the local API).
function getApi2Host(): string | null {
  const hostname = window.location.hostname.toLowerCase()
  if (hostname === 'localhost' || hostname === '127.0.0.1') return null
  const envMatch = hostname.match(/(?:^|\.)(beta[a-z0-9-]*)\.contractfinderpro\.com$/)
  if (envMatch) return `api2-${envMatch[1]}.contractfinderpro.com`
  return 'api2.contractfinderpro.com'
}

function getEndpoint(): string {
  const host = getApi2Host()
  // Localhost — return an empty base so Vite's dev-server proxy forwards
  // relative paths to localhost:10281 (avoids CORS).
  if (host === null) return ''
  return `https://${host}`
}

function getBearerToken(): string {
  const match = document.cookie
    .split(';')
    .map(c => c.trim())
    .find(c => c.startsWith('cfp_access_token='))
  if (!match) return ''
  return match.slice('cfp_access_token='.length)
}

class TaApi {
  private get endpoint(): string {
    return getEndpoint()
  }

  private get authHeader(): HeadersInit {
    const token = getBearerToken()
    return token ? { Authorization: `Bearer ${token}` } : {}
  }

  // Issue a request, transparently retrying once with a refreshed
  // bearer token if the first attempt returns 401. The refresh uses
  // the cfp_refresh_token cookie via `tryRefreshToken`. If the
  // refresh itself fails - or the retry still comes back 401 - the
  // user is bounced to /login (their refresh token has also expired).
  // Callers see the final Response and can branch on its status as
  // before; we never throw from auth handling.
  //
  // `buildInit` returns a fresh RequestInit each call so the retry
  // picks up the newly-refreshed bearer token (the cookie has been
  // updated by tryRefreshToken before we re-invoke buildInit).
  private async fetchWithAuthRetry(url: string, buildInit: () => RequestInit): Promise<Response> {
    let res = await fetch(url, buildInit())
    if (res.status !== 401) return res
    const refreshed = await tryRefreshToken()
    if (!refreshed) {
      redirectToLogin()
      return res
    }
    res = await fetch(url, buildInit())
    if (res.status === 401) {
      redirectToLogin()
    }
    return res
  }

  async get(path: string, params?: Record<string, string>): Promise<any> {
    let url = `${this.endpoint}${path}`
    if (params && Object.keys(params).length > 0) {
      const qs = new URLSearchParams(params).toString()
      url = `${url}?${qs}`
    }
    const response = await this.fetchWithAuthRetry(url, () => ({
      method: 'GET',
      headers: { ...this.authHeader },
      credentials: 'include',
    }))
    if (response.status === 401) return
    return response.json()
  }

  async post(path: string, body: unknown): Promise<any> {
    const url = `${this.endpoint}${path}`
    const response = await this.fetchWithAuthRetry(url, () => ({
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.authHeader,
      },
      credentials: 'include',
      body: JSON.stringify(body),
    }))
    if (response.status === 401) return
    return response.json()
  }

  // Build the WebSocket URL for a streaming endpoint. In dev we go through
  // the same origin so Vite's proxy forwards the upgrade to localhost:10281;
  // in prod we hit the api2 host directly. Native WebSockets can't send an
  // Authorization header, so the bearer travels as a `cfp_access_token`
  // query-string parameter that the server's `ResolveEmail` reads directly
  // from `Request.Query`.
  taskSocketUrl(path: string): string {
    const token = getBearerToken()
    const separator = path.includes('?') ? '&' : '?'
    const pathWithToken = token
      ? `${path}${separator}cfp_access_token=${encodeURIComponent(token)}`
      : path
    const host = getApi2Host()
    if (host === null) {
      const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      return `${proto}//${window.location.host}${pathWithToken}`
    }
    return `wss://${host}${pathWithToken}`
  }

  // WebSocket driver for the qastream / chatstream endpoints. Sends `body` as
  // the first message, then yields each `data:` payload string (the text after
  // "data: ") from the SSE-framed frames the server emits - so consumers keep
  // their existing chunk-parsing loop, just iterating this instead of reading a
  // fetch ReadableStream. Yields the literal "[DONE]" sentinel.
  // Aborting `signal` closes the socket and makes the generator throw an
  // AbortError, matching fetch's behaviour so manual-stop handling is unchanged.
  async *openStreamSocket(path: string, body: unknown, signal?: AbortSignal): AsyncGenerator<string> {
    const ws = new WebSocket(this.taskSocketUrl(path))

    const queue: string[] = []
    let done = false
    let failure: Error | null = null
    let notify: (() => void) | null = null
    const wake = () => { const n = notify; notify = null; n?.() }

    let buffer = ''
    const onText = (text: string) => {
      buffer += text
      const parts = buffer.split('\n\n')
      buffer = parts.pop() ?? ''
      for (const part of parts) {
        for (const line of part.split('\n')) {
          if (!line.startsWith('data:')) continue
          queue.push(line.slice(5).trim())
        }
      }
      wake()
    }

    ws.onopen = () => { try { ws.send(JSON.stringify(body)) } catch { /* closing */ } }
    ws.onmessage = (e) => { if (typeof e.data === 'string') onText(e.data) }
    ws.onerror = () => { if (!failure) failure = new Error('qa socket error'); wake() }
    ws.onclose = () => { done = true; wake() }

    const onAbort = () => {
      failure = new DOMException('The operation was aborted.', 'AbortError')
      try { ws.close() } catch { /* closing */ }
      wake()
    }
    if (signal) {
      if (signal.aborted) onAbort()
      else signal.addEventListener('abort', onAbort, { once: true })
    }

    try {
      while (true) {
        if (queue.length > 0) { yield queue.shift() as string; continue }
        if (failure) throw failure
        if (done) return
        await new Promise<void>(resolve => { notify = resolve })
      }
    } finally {
      signal?.removeEventListener('abort', onAbort)
      try { ws.close() } catch { /* already closing */ }
    }
  }

  async postForm(path: string, formData: FormData): Promise<any> {
    const url = `${this.endpoint}${path}`
    const response = await this.fetchWithAuthRetry(url, () => ({
      method: 'POST',
      headers: { ...this.authHeader },
      credentials: 'include',
      body: formData,
    }))
    if (response.status === 401) return
    return response.json()
  }

  async download(path: string, params?: Record<string, string>): Promise<{ blob: Blob; filename: string }> {
    let url = `${this.endpoint}${path}`
    if (params && Object.keys(params).length > 0) {
      url = `${url}?${new URLSearchParams(params)}`
    }
    const response = await this.fetchWithAuthRetry(url, () => ({
      method: 'GET',
      headers: { ...this.authHeader },
      credentials: 'include',
    }))
    if (response.status === 401) throw new Error('Unauthorized')
    const disposition = response.headers.get('Content-Disposition') ?? ''
    const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
    const filename = match ? match[1].replace(/['"]/g, '') : 'download'
    return { blob: await response.blob(), filename }
  }
}

export default new TaApi()
